/**
 * Жизненный цикл исходящего звонка.
 *
 * Карточка, по которой звонит диспетчер, уезжает на станцию кастомным SIP-заголовком
 * X-Assignment-Id (спека §7.1). Заголовок формирует браузер, поэтому он не является
 * доказательством: телефония на StasisStart сверяет назначение с эндпоинтом и номером
 * (§7.3). Здесь наша задача — только корректно его сформировать и провести сессию
 * по состояниям, не потеряв причину завершения.
 */
import { systemScheduler } from './ports.js';
import { ICE_GATHERING_TIMEOUT_MS } from './ice-watchdog.js';
/** Имена заголовков объявлены здесь и в apps/telephony/src/ari/constants.ts — менять парой */
export const ASSIGNMENT_HEADER = 'X-Assignment-Id';
export const CALL_TRACE_HEADER = 'X-Call-Trace-Id';
/** Карусель (§7.11): станция звонит пульту сама; «1» — снять трубку без человека */
export const CAROUSEL_AUTO_HEADER = 'X-CC-Auto';
/** Номер абонента, который уже на линии, — для карточки на пульте */
export const CAROUSEL_PHONE_HEADER = 'X-CC-Phone';
export function buildCallHeaders(request) {
    return [
        `${ASSIGNMENT_HEADER}: ${request.assignmentId}`,
        `${CALL_TRACE_HEADER}: ${request.traceId}`,
    ];
}
/** SIP-состояние сессии → состояние, которое видит диспетчер */
export function toCallState(state) {
    switch (state) {
        case 'establishing':
            return 'dialing';
        case 'ringing':
            return 'ringing';
        case 'established':
            return 'in_call';
        case 'terminated':
            return 'ended';
    }
}
/**
 * Одна исходящая нога диспетчера. Создаётся через place(), живёт до hangup()
 * или до terminated от станции; повторно не используется.
 */
export class OutboundCall {
    assignmentId;
    phone;
    traceId;
    #session;
    #hooks;
    #scheduler;
    #unsubscribe = null;
    #state = 'dialing';
    /** Причина завершения, если её задали до прихода terminated (отбой, сеть) */
    #pendingReason = null;
    constructor(session, request, deps) {
        this.#session = session;
        this.assignmentId = request.assignmentId;
        this.phone = request.phone;
        this.traceId = request.traceId;
        this.#hooks = deps.hooks;
        this.#scheduler = deps.scheduler ?? systemScheduler;
    }
    static async place(stack, request, deps) {
        const options = {
            extraHeaders: buildCallHeaders(request),
            iceGatheringTimeoutMs: ICE_GATHERING_TIMEOUT_MS,
            ...(deps.inputDeviceId !== undefined ? { inputDeviceId: deps.inputDeviceId } : {}),
        };
        const session = await stack.invite(request.phone, options);
        const call = new OutboundCall(session, request, deps);
        call.#attach();
        return call;
    }
    /**
     * Обёртка над уже принятой ВХОДЯЩЕЙ сессией (карусель, §7.11): станция сама
     * позвонила пульту, INVITE принят — дальше звонок ведётся теми же рельсами,
     * что и исходящий: состояния, ICE-сторож, причина завершения.
     */
    static fromSession(session, request, deps) {
        const call = new OutboundCall(session, request, deps);
        call.#attach();
        return call;
    }
    get id() {
        return this.#session.id;
    }
    getState() {
        return this.#state;
    }
    /** Отбой по кнопке диспетчера */
    async hangup(reason = 'hangup_local') {
        this.#pendingReason = reason;
        await this.#session.hangup();
    }
    /**
     * Выключение микрофона. Дорожка не убирается из сессии, а глушится:
     * removeTrack вызвал бы re-INVITE и паузу в разговоре.
     */
    setMuted(muted) {
        const pc = this.#session.getPeerConnection();
        if (!pc)
            return;
        for (const sender of pc.getSenders()) {
            if (sender.track && sender.track.kind === 'audio')
                sender.track.enabled = !muted;
        }
    }
    /** Смена гарнитуры в разговоре: подмена дорожки без re-INVITE (спека §7.9) */
    async replaceInputTrack(track) {
        const pc = this.#session.getPeerConnection();
        if (!pc)
            return;
        const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
        if (!sender)
            return;
        await sender.replaceTrack(track);
    }
    /** Две попытки ICE restart перед тем, как признать звонок потерянным (§7.9) */
    async restartIce() {
        await this.#session.restartIce();
    }
    #attach() {
        const pc = this.#session.getPeerConnection();
        if (pc)
            this.#hooks.onPeerConnection?.(pc);
        const remote = this.#session.getRemoteStream();
        if (remote)
            this.#hooks.onRemoteStream?.(remote);
        this.#unsubscribe = this.#session.onStateChange((sipState) => {
            const next = toCallState(sipState);
            const at = this.#scheduler.now();
            if (sipState === 'established') {
                const established = this.#session.getPeerConnection();
                if (established)
                    this.#hooks.onPeerConnection?.(established);
                const stream = this.#session.getRemoteStream();
                if (stream)
                    this.#hooks.onRemoteStream?.(stream);
            }
            if (next === 'ended') {
                this.#state = 'ended';
                this.#unsubscribe?.();
                this.#unsubscribe = null;
                // Причина без данных станции неотличима: 'hangup_remote' — значение по умолчанию,
                // точную диспозицию всё равно считает сервер по правилам disposition_rules (§7.5).
                this.#hooks.onEnded(this.#pendingReason ?? 'hangup_remote', at);
                return;
            }
            this.#state = next;
            this.#hooks.onStateChange(next, at);
        });
    }
}
//# sourceMappingURL=outbound-call.js.map